'use client';

import { BETA_ACCESS, useBetaAccess } from '@/hooks/useBetaAccess';
import React, { useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { useRouter } from 'next/navigation';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { ToastContext } from '@/app/components/Toast';

const CHUNK_RELOAD_KEY = 'chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 10000;

const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' || /Loading chunk [\w-]+ failed/i.test(error?.message || '');

export default function Error({ error, reset }) {
  const router = useRouter();
  const toast = useContext(ToastContext);
  const canCopyError = useBetaAccess(BETA_ACCESS.errorCopyDetails);

  // A deploy replaced the chunks this tab was built against; reload once to pick up the new build.
  useEffect(() => {
    if (!isChunkLoadError(error)) return;
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }, [error]);

  const statusCode = error?.errorCode || error?.status || error?.response?.status;
  const isApiError = !!(statusCode || error?.errorRef || error?.formattedError);
  const isDev = process.env.NODE_ENV === 'development';
  const message = isDev
    ? getErrorMessage(error)
    : isApiError
      ? getErrorMessage(error)
      : 'The page encountered an unexpected error. Please try again or go home.';

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    router.push('/dashboard');
  };

  const handleCopyError = async () => {
    const errorDetails = {
      statusCode: statusCode || null,
      message,
      rawMessage: getErrorMessage(error),
      stack: error?.stack || 'No stack trace available',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
      toast.success('Error details copied to clipboard.');
    } catch {
      toast.error('Failed to copy error details.');
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      <Box sx={{
        textAlign: 'center',
        maxWidth: 440,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'error.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          {statusCode ? (
            <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
              {statusCode}
            </Typography>
          ) : (
            <Typography variant="h4" sx={{ color: 'error.main' }}>!</Typography>
          )}
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}>
          {statusCode ? `Error ${statusCode}` : 'An error occurred'}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.primary', mb: 2 }}>
          {message}
        </Typography>

        <Stack spacing={2} direction="row" justifyContent="center">
          <Button
            variant="contained"
            startIcon={<Iconify icon={UI_ICONS.refresh} width={24} />}
            onClick={handleReload}
            sx={{ textTransform: 'none' }}
          >
            Try Again
          </Button>

          <Button
            variant="outlined"
            startIcon={<Iconify icon={UI_ICONS.home} width={24} />}
            onClick={handleGoHome}
            sx={{ textTransform: 'none' }}
          >
            Go Home
          </Button>

          {canCopyError && (
            <Button
              variant="text"
              startIcon={<Iconify icon={UI_ICONS.bugReport} width={24} />}
              onClick={handleCopyError}
              sx={{ textTransform: 'none' }}
            >
              Copy Error
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
